import { FC, useContext, useEffect, useRef, useState, useCallback } from "react";
import { Context } from "../../..";
import "./UserPopup.css";
import Avatar, { AvatarSize } from "../Avatar/Avatar";
import EmailImage from "../../../assets/email.svg";
import useOutsideAlerter from "../../../hooks/useOutsideAlert";
import Loader from "../Loader/Loader";
import { IFullUserInfo } from "../../../common/api_interfaces/user/IUserInfo";

interface UserPopupProps {
    userId: number;
    onOverlayClick: () => void;
    onLoaded: () => void;
}

const UserPopup: FC<UserPopupProps> = ({ userId, onOverlayClick, onLoaded }) => {
    const { store } = useContext(Context);
    const [user, setUser] = useState<IFullUserInfo | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const outsideAlerterRef = useOutsideAlerter<HTMLDivElement>(onOverlayClick);

    const fetchUser = useCallback(async () => {
        if (!userId) return;
        
        setIsLoading(true);
        try {
            const userRes = await store.getUser(userId);
            if (userRes) {
                setUser(userRes);
            }
            onLoaded();
        } catch (error) {
            console.error('Failed to fetch user:', error);
        } finally {
            setIsLoading(false);
        }
    }, [userId, store, onLoaded]);

    useEffect(() => {
        fetchUser();
    }, [fetchUser]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (outsideAlerterRef.current && !outsideAlerterRef.current.contains(event.target as Node)) {
                onOverlayClick();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [onOverlayClick]);

    const handleClick = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
    }, []);

    const formattedName = user 
        ? `${user.surname ?? ''} ${user.name ?? ''}`.trim()
        : '';

    const formattedRole = user?.role?.name ?? '';
    const formattedEmail = user?.email ?? '';

    return (
        <div onClick={handleClick}>
            <div className="user-popup-container" ref={outsideAlerterRef}>
                {isLoading ? (
                    <div className="user-popup-loading user-popup-loading-enter-active">
                        <Loader overlay={false}/>
                    </div>
                ) : (
                    <>
                        <div className="top-panel user-popup-content-enter-active">
                            <div>
                                <Avatar 
                                    userId={user?.id} 
                                    size={AvatarSize.xl} 
                                />
                            </div>
                            <div className="user-info-container">
                                <p className="user-info-name">{formattedName}</p>
                                <p className="user-info-role">{formattedRole}</p>
                            </div>
                        </div>
                        <div className="bottom-panel">
                            <div className="email-container">
                                <img
                                    src={EmailImage}
                                    alt="Электронная почта"
                                    width="18"
                                    height="18"
                                    loading="lazy"
                                />
                                {formattedEmail}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default UserPopup;